import type { Href } from "expo-router";
import { router, useFocusEffect } from "expo-router";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ActivityIndicator, Platform, Pressable, ScrollView, Text, TextInput, View, type TextStyle, type ViewStyle } from "react-native";

import {
  categoryForAction,
  listAdminAuditLog,
  listAuditActors,
  listAuditEntityTypes,
  type AdminAuditLogRow,
  type AuditCategory,
} from "../../../lib/auditLog";
import { exportRowsAsCsv, fmtCellDate, todayDateForFilename } from "../../../lib/csvExport";
import { useAdminListFilter } from "../../../lib/useAdminListFilter";
import { COLORS, FONTS } from "../../../lib/brand";
import { PANE_THEMES } from "../../../components/brand/paneThemes";
import {
  AdminButton,
  AdminEmpty,
  AdminError,
  AdminInput,
  AdminLoading,
  AdminPageHeader,
  FilterChip as SharedFilterChip,
} from "../../../components/admin/ui";

const THEME = PANE_THEMES.Admin;

const BLACK = THEME.pageBg;
const FOREST_MID = THEME.surfaceRaisedBg;
const FOREST_DEEPER = THEME.altBg;
const AMBER = THEME.accent;
const SAGE = COLORS.SAGE.hex;
const LINEN = THEME.textOnSurface;

const W = "rgba(255,255,255,1)";
const WS = "rgba(255,255,255,0.78)";
const WM = "rgba(255,255,255,0.55)";
const WL = "rgba(255,255,255,0.28)";
const RED = THEME.destructive;
const BORDER = "rgba(255,255,255,0.07)";

const TZ = "America/New_York";

const CATEGORY_LABELS: Record<AuditCategory, string> = {
  all: "All",
  roles: "Roles",
  team: "Team",
  competitions: "Competitions",
  positions: "Positions",
  winners: "Winners",
  disbursements: "Disbursements",
  offers: "Offers",
  inquiries: "Inquiries",
  announcements: "Announcements",
  invites: "Invites",
  domains: "Domains",
  exports: "Exports",
  impersonate: "Impersonate",
  other: "Other",
};

const PAGE_SIZE = 50;

function fmtTs(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return `${d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: TZ })} · ${d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", timeZone: TZ })}`;
}

function fromIsoForRange(range: string): string | null {
  if (range === "today") {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d.toISOString();
  }
  if (range === "7d") return new Date(Date.now() - 7 * 86_400_000).toISOString();
  if (range === "30d") return new Date(Date.now() - 30 * 86_400_000).toISOString();
  return null;
}

function categoryTone(cat: AuditCategory): string {
  switch (cat) {
    case "roles":
      return AMBER;
    case "team":
      return SAGE;
    case "competitions":
      return AMBER;
    case "positions":
      return SAGE;
    case "winners":
      return AMBER;
    case "disbursements":
      return AMBER;
    case "offers":
      return SAGE;
    case "inquiries":
      return SAGE;
    case "announcements":
      return SAGE;
    case "invites":
      return AMBER;
    case "domains":
      return WS;
    case "exports":
      return WS;
    case "impersonate":
      return RED;
    default:
      return WM;
  }
}

export default function AdminAuditLogScreen() {
  const filter = useAdminListFilter({
    keys: ["q", "range", "category", "actor", "entity"],
    defaults: { q: "", range: "7d", category: "all", actor: "any", entity: "any" },
  });
  const search = filter.get("q");
  const range = filter.get("range");
  const category = filter.get("category") as AuditCategory;
  const actor = filter.get("actor");
  const entity = filter.get("entity");

  const [searchInput, setSearchInput] = useState(search);
  useEffect(() => setSearchInput(search), [search]);
  useEffect(() => {
    const t = setTimeout(() => {
      if (searchInput !== search) filter.set({ q: searchInput });
    }, 200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  const [rows, setRows] = useState<AdminAuditLogRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [actors, setActors] = useState<{ actor_id: string; email: string }[]>([]);
  const [entityTypes, setEntityTypes] = useState<string[]>([]);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const hasLoadedRef = useRef(false);

  const filterArgs = useMemo(
    () => ({
      search: search.trim() || undefined,
      fromIso: fromIsoForRange(range),
      category,
      actorId: actor !== "any" ? actor : null,
      entityType: entity !== "any" ? entity : null,
    }),
    [search, range, category, actor, entity]
  );

  const load = useCallback(
    async (mode: "first" | "more") => {
      if (mode === "first") {
        if (!hasLoadedRef.current) setLoading(true);
        setExpandedId(null);
      } else {
        setLoadingMore(true);
      }
      setErr(null);
      try {
        const offset = mode === "more" ? rows.length : 0;
        const list = await listAdminAuditLog({
          ...filterArgs,
          limit: PAGE_SIZE,
          offset,
        });
        setHasMore(list.length === PAGE_SIZE);
        setRows((prev) => (mode === "more" ? [...prev, ...list] : list));
      } catch (e) {
        setErr(e instanceof Error ? e.message : "Failed to load audit log");
      } finally {
        hasLoadedRef.current = true;
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [filterArgs, rows.length]
  );

  useFocusEffect(
    useCallback(() => {
      void load("first");
      void (async () => {
        const [a, e] = await Promise.all([listAuditActors(), listAuditEntityTypes()]);
        setActors(a);
        setEntityTypes(e);
      })();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterArgs])
  );

  return (
    <ScrollView style={{ flex: 1, backgroundColor: BLACK }} contentContainerStyle={{ paddingBottom: 32 }}>
      <View style={{ padding: 24, maxWidth: 1080 }}>
        <AdminPageHeader
          theme={THEME}
          eyebrow="Platform · Audit log"
          title="Activity log"
          subtitle="Every admin action — who, what, when."
          actions={
            <AdminButton
              theme={THEME}
              label="Export CSV (visible page)"
              variant="ghost"
              size="sm"
              onPress={() =>
                exportRowsAsCsv({
                  filename: `audit_log_${todayDateForFilename()}.csv`,
                  auditPage: "audit-log",
                  columns: [
                    { header: "Timestamp", get: (r: AdminAuditLogRow) => fmtCellDate(r.created_at) },
                    { header: "Actor email", get: (r: AdminAuditLogRow) => r.actor_email ?? "" },
                    { header: "Actor role", get: (r: AdminAuditLogRow) => r.actor_role ?? "" },
                    { header: "Action", get: (r: AdminAuditLogRow) => r.action },
                    { header: "Entity type", get: (r: AdminAuditLogRow) => r.entity_type },
                    { header: "Entity id", get: (r: AdminAuditLogRow) => r.entity_id ?? "" },
                    { header: "Summary", get: (r: AdminAuditLogRow) => r.summary },
                    { header: "Diff", get: (r: AdminAuditLogRow) => (r.diff ? JSON.stringify(r.diff) : "") },
                  ],
                  rows,
                })
              }
            />
          }
        />

        {err ? <View style={{ marginBottom: 14 }}><AdminError theme={THEME} message={err} /></View> : null}

        {/* Filter bar */}
        <View
          style={{
            marginTop: 18,
            padding: 12,
            borderRadius: 6,
            backgroundColor: FOREST_MID,
            borderWidth: 1,
            borderColor: BORDER,
            gap: 10,
          }}
        >
          <AdminInput theme={THEME} value={searchInput} onChangeText={setSearchInput} placeholder="Search actor email or summary…" autoCapitalize="none" />
          <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 6, alignItems: "center" }}>
            <Text style={{ fontFamily: FONTS.bodyMedium, fontSize: 11, color: WM }}>Range:</Text>
            <Chip label="Today" active={range === "today"} onPress={() => filter.set({ range: "today" })} />
            <Chip label="7 days" active={range === "7d"} onPress={() => filter.set({ range: "7d" })} />
            <Chip label="30 days" active={range === "30d"} onPress={() => filter.set({ range: "30d" })} />
            <Chip label="All time" active={range === "all"} onPress={() => filter.set({ range: "all" })} />
          </View>
          <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 6, alignItems: "center" }}>
            <Text style={{ fontFamily: FONTS.bodyMedium, fontSize: 11, color: WM }}>Action:</Text>
            {(Object.keys(CATEGORY_LABELS) as AuditCategory[]).map((k) => (
              <Chip
                key={k}
                label={CATEGORY_LABELS[k]}
                active={category === k}
                onPress={() => filter.set({ category: k })}
              />
            ))}
          </View>
          {actors.length > 0 ? (
            <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 6, alignItems: "center" }}>
              <Text style={{ fontFamily: FONTS.bodyMedium, fontSize: 11, color: WM }}>Actor:</Text>
              <Chip label="Any" active={actor === "any"} onPress={() => filter.set({ actor: "any" })} />
              {actors.slice(0, 8).map((a) => (
                <Chip
                  key={a.actor_id}
                  label={a.email}
                  active={actor === a.actor_id}
                  onPress={() => filter.set({ actor: a.actor_id })}
                />
              ))}
            </View>
          ) : null}
          {entityTypes.length > 0 ? (
            <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 6, alignItems: "center" }}>
              <Text style={{ fontFamily: FONTS.bodyMedium, fontSize: 11, color: WM }}>Entity:</Text>
              <Chip label="Any" active={entity === "any"} onPress={() => filter.set({ entity: "any" })} />
              {entityTypes.slice(0, 10).map((e) => (
                <Chip
                  key={e}
                  label={e}
                  active={entity === e}
                  onPress={() => filter.set({ entity: e })}
                />
              ))}
            </View>
          ) : null}
        </View>

        {loading ? (
          <View style={{ marginTop: 18 }}><AdminLoading theme={THEME} /></View>
        ) : rows.length === 0 ? (
          <View style={{ marginTop: 18 }}>
            <AdminEmpty theme={THEME} title="No matching activity." body="Widen your filters or wait for someone to make an admin change." />
          </View>
        ) : (
          <View style={{ marginTop: 18, gap: 8 }}>
            {rows.map((row) => (
              <AuditRow
                key={row.id}
                row={row}
                expanded={expandedId === row.id}
                onPress={() => setExpandedId((cur) => (cur === row.id ? null : row.id))}
              />
            ))}
            {hasMore ? (
              <Pressable
                onPress={() => void load("more")}
                disabled={loadingMore}
                style={[
                  {
                    marginTop: 6,
                    alignSelf: "center",
                    paddingHorizontal: 16,
                    paddingVertical: 10,
                    borderRadius: 3,
                    borderWidth: 1,
                    borderColor: BORDER,
                    backgroundColor: "rgba(255,255,255,0.02)",
                  } as ViewStyle,
                  Platform.OS === "web" && !loadingMore ? ({ cursor: "pointer" } as const) : null,
                ]}
              >
                <Text
                  style={{
                    fontFamily: FONTS.bodyBold,
                    fontSize: 11,
                    letterSpacing: 1.4,
                    textTransform: "uppercase",
                    color: WS,
                  }}
                >
                  {loadingMore ? "Loading…" : "Load more"}
                </Text>
              </Pressable>
            ) : null}
          </View>
        )}
      </View>
    </ScrollView>
  );
}

function AuditRow({
  row,
  expanded,
  onPress,
}: {
  row: AdminAuditLogRow;
  expanded: boolean;
  onPress: () => void;
}) {
  const cat = categoryForAction(row.action);
  const tone = categoryTone(cat);
  return (
    <Pressable
      onPress={onPress}
      style={[
        {
          padding: 14,
          borderRadius: 6,
          backgroundColor: FOREST_MID,
          borderWidth: 1,
          borderColor: expanded ? "rgba(181,135,58,0.32)" : BORDER,
        } as ViewStyle,
        Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null,
      ]}
    >
      <View style={{ flexDirection: "row", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
        <View
          style={{
            paddingHorizontal: 8,
            paddingVertical: 3,
            borderRadius: 999,
            borderWidth: 1,
            borderColor: tone,
            backgroundColor: "rgba(255,255,255,0.04)",
          }}
        >
          <Text
            style={{
              fontFamily: FONTS.bodyBold,
              fontSize: 9,
              letterSpacing: 1.2,
              textTransform: "uppercase",
              color: tone,
            }}
          >
            {row.action}
          </Text>
        </View>
        <View style={{ flex: 1, minWidth: 0 }}>
          <Text style={{ fontFamily: FONTS.bodyMedium, fontSize: 13, color: LINEN }} numberOfLines={2}>
            {row.summary}
          </Text>
          <Text style={{ marginTop: 2, fontFamily: FONTS.body, fontSize: 11.5, color: WS }}>
            {row.actor_email ?? "(unknown actor)"} · {row.actor_role ?? "—"}
          </Text>
          <Text style={{ marginTop: 1, fontFamily: FONTS.body, fontSize: 10.5, color: WM }}>
            {row.entity_type}
            {row.entity_id ? ` · ${row.entity_id.slice(0, 8)}…` : ""}
          </Text>
        </View>
        <Text style={{ fontFamily: FONTS.body, fontSize: 11, color: WL }}>{fmtTs(row.created_at)}</Text>
      </View>
      {expanded ? (
        <View style={{ marginTop: 10, gap: 8 }}>
          <View
            style={{
              padding: 12,
              backgroundColor: FOREST_DEEPER,
              borderWidth: 1,
              borderColor: BORDER,
              borderRadius: 4,
            }}
          >
            <Text
              style={{
                fontFamily: FONTS.bodyBold,
                fontSize: 9,
                letterSpacing: 1.4,
                textTransform: "uppercase",
                color: WM,
                marginBottom: 6,
              }}
            >
              Diff
            </Text>
            <Text
              selectable
              style={{
                fontFamily: Platform.OS === "web" ? ("'JetBrains Mono', Menlo, monospace" as string) : "Menlo",
                fontSize: 11,
                color: LINEN,
                lineHeight: 16,
              }}
            >
              {row.diff ? JSON.stringify(row.diff, null, 2) : "—"}
            </Text>
          </View>
          {row.entity_id ? <ViewEntityLink row={row} /> : null}
        </View>
      ) : null}
    </Pressable>
  );
}

function ViewEntityLink({ row }: { row: AdminAuditLogRow }) {
  const href = entityHref(row);
  if (!href) return null;
  return (
    <Pressable
      onPress={() => router.push(href)}
      style={[
        { alignSelf: "flex-start", paddingVertical: 6 } as ViewStyle,
        Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null,
      ]}
    >
      <Text style={{ fontFamily: FONTS.bodyBold, fontSize: 11, letterSpacing: 1.4, textTransform: "uppercase", color: AMBER }}>
        View entity ↗
      </Text>
    </Pressable>
  );
}

function entityHref(row: AdminAuditLogRow): Href | null {
  if (!row.entity_id) return null;
  const id = row.entity_id;
  switch (row.entity_type) {
    case "user_profiles":
      return `/admin/student/${id}` as Href;
    case "competitions":
      return `/client/competition/${id}` as Href;
    case "campus_admins":
      return `/admin/campus-admins` as Href;
    case "invites":
      return `/admin/invites` as Href;
    case "announcements":
      return `/admin/announcements` as Href;
    case "allowed_email_domains":
      return `/admin/domains` as Href;
    default:
      return null;
  }
}

function Chip({ label, active, onPress }: { label: string; active: boolean; onPress: () => void }) {
  return <SharedFilterChip theme={THEME} label={label} active={active} onPress={onPress} />;
}
